import { RouteComponentProps } from 'react-router'
import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { ClaimFooter } from '../components/ClaimFooter'
import ClaimHeader from '../CodeClaimPage/components/ClaimHeader'
import WebsiteClaimAddressForm from './WebsiteClaimAddressForm'
import { claimSecret, getEventById, getSecretWebsiteByName, HashClaim, PoapEvent, Secret } from '../api'
import ClaimLoading from '../CodeClaimPage/components/ClaimLoading'
import WebsiteClaimOutOfCodes from './WebsiteClaimOutOfCodes'

import EmptyBadge from '../images/empty-badge.svg'
import WebsiteClaimError from './WebsiteClaimError'
import { ROUTES } from '../lib/constants'

type WebsiteError = {
  title: string
  description: any[]
  onBack: () => void
}

const WebsiteClaimPage: React.FC<RouteComponentProps<{ website: string }>> = ({ match, history }) => {
  const [websiteName] = useState<string | undefined>(match.params.website)
  const [website, setWebsite] = useState<Secret | undefined>(undefined)
  const [event, setEvent] = useState<PoapEvent | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [outOfCodes, setOutOfCodes] = useState<boolean>(false)
  const [error, setError] = useState<WebsiteError | undefined>(undefined)
  const [formLoading, setFormLoading] = useState<boolean>(false)

  useEffect(() => {
    if (websiteName && websiteName !== '') {
      fetchWebsite().then()
    }
    // eslint-disable-next-line
  }, [websiteName])

  const websiteNotFoundError: WebsiteError = {
    title: 'Website not found',
    description: [
      'We are sorry, the website you requested could not be found. Is this your website? ',
      {
        type: 'link',
        text: 'Contact support',
        href: 'https://poap.xyz/form',
      },
    ],
    onBack: () => {
      history.push(ROUTES.websiteClaim.home.path)
    },
  }

  const mintWindowNotStartedError: WebsiteError = {
    title: "Minting hasn't started yet!",
    description: [
      'The mint window for this drop has not yet begun. Come back later! If this is your drop, ',
      {
        type: 'link',
        text: 'log in',
        href: 'https://drops.poap.xyz',
      },
      ' to adjust the mint window, or contact support.',
    ],
    onBack: () => {
      history.push(ROUTES.websiteClaim.home.path)
    },
  }

  const mintWindowEndedError: WebsiteError = {
    title: 'Minting has ended!',
    description: [
      'The mint window for this drop has closed. If this is your drop, ',
      {
        type: 'link',
        text: 'log in',
        href: 'https://drops.poap.xyz',
      },
      ' to adjust the mint window, or contact support.',
    ],
    onBack: () => {
      history.push(ROUTES.websiteClaim.home.path)
    },
  }

  const mintingDisabledError: WebsiteError = {
    title: 'Minting is disabled!',
    description: [
      'The owner of this drop has disabled minting. If this is your drop, ',
      {
        type: 'link',
        text: 'log in',
        href: 'https://drops.poap.xyz',
      },
      ' to reactivate minting, or contact support.',
    ],
    onBack: () => {
      history.push(ROUTES.websiteClaim.home.path)
    },
  }

  const dropExpiredError: WebsiteError = {
    title: 'Drop expired!',
    description: [
      'This drop has expired and cannot be minted any more. If this is your drop and you need help, contact support.',
    ],
    onBack: () => {
      history.push(ROUTES.websiteClaim.home.path)
    },
  }

  const fetchWebsite = async () => {
    if (websiteName) {
      const qrHash = localStorage.getItem(websiteName)

      if (qrHash && qrHash !== '') {
        history.push(`/claim/${qrHash}`)
        return
      }

      try {
        const response = await getSecretWebsiteByName(websiteName)

        if (response.ok) {
          setWebsite(await response.json())
        } else if (response.status === 404 || response.status === 409) {
          const error: any = await response.json()

          if (error && error.message) {
            if (error.message.includes('Secret is not active')) {
              setError(mintingDisabledError)
            } else if (error.message.includes('Cannot claim secret: mint window has not started yet.')) {
              setError(mintWindowNotStartedError)
            } else if (error.message.includes('Cannot claim secret: mint window has already ended.')) {
              setError(mintWindowEndedError)
            } else if (error.message.includes('Invalid secret: parent event expired.')) {
              setError(dropExpiredError)
            } else setError(websiteNotFoundError)
          } else setError(websiteNotFoundError)
        }
        setLoading(false)
      } catch (e) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (website) {
      fetchEvent().then()
    }
    // eslint-disable-next-line
  }, [website])

  const fetchEvent = async () => {
    if (website) {
      const _event = await getEventById(website.event_id)
      setEvent(_event ? _event : undefined)
    }
  }

  useEffect(() => {
    if (event) {
      setLoading(false)
    }
  }, [event])

  const image = useMemo<string>(() => event?.image_url || EmptyBadge, [event])
  const originalImage = useMemo<string>(() => event?.original_image_url || image, [event, image])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const title = useMemo<string>(() => event?.name || websiteName || '', [event])

  const submitError = {
    title: 'Something went wrong',
    description: [
      'We are not able to serve your request right now,' +
        ' either the service is unavailable or your request could not be verified.' +
        ' If you think this is an error please submit a report to this ',
      {
        type: 'link',
        text: 'form',
        href: 'https://poap.xyz/form',
      },
    ],
    onBack: () => {
      setError(undefined)
    },
  }

  const alreadyClaimedError: WebsiteError = {
    title: 'Address already claim this POAP',
    description: ['This website has already been claimed with this address.'],
    onBack: () => {
      history.push(ROUTES.websiteClaim.home.path)
    },
  }

  const handleClaimAddressFormOnSubmit = async (address: string) => {
    setFormLoading(true)

    if (!websiteName) {
      return
    }

    try {
      const response = await claimSecret(address, websiteName)

      // error and not 409: already claimed
      if (!response.ok && response.status !== 409) {
        const error = await response.json()
        const errorMessage = error && error.message ? error.message : ''

        if (response.status === 400) {
          if (errorMessage.includes('already claimed')) {
            setError(alreadyClaimedError)
          } else {
            setOutOfCodes(true)
          }
        } else if (errorMessage.includes('Invalid secret: parent event expired.')) {
          setError(dropExpiredError)
        } else if (errorMessage.includes('Secret is not active')) {
          setError(mintingDisabledError)
        } else if (errorMessage.includes('Cannot claim secret: mint window has not started yet.')) {
          setError(mintWindowNotStartedError)
        } else if (errorMessage.includes('Cannot claim secret: mint window has already ended.')) {
          setError(mintWindowEndedError)
        } else {
          setError(submitError)
        }

        setFormLoading(false)
        return
      }

      const qrClaim: HashClaim = await response.json()
      localStorage.setItem(websiteName, qrClaim.qr_hash)
      history.push(`/claim/${qrClaim.qr_hash}`)
    } catch (e) {
      setError(submitError)
    }

    setFormLoading(false)
  }

  return (
    <div className={'code-claim-page website-claim-page-container'}>
      <ClaimHeader title={title} image={image} originalImage={originalImage} claimed={false} showEventInfo={!error} />
      {loading && <ClaimLoading />}
      {!loading && !outOfCodes && !error && (
        <WebsiteClaimAddressForm onSubmit={handleClaimAddressFormOnSubmit} loading={formLoading} />
      )}
      {outOfCodes && <WebsiteClaimOutOfCodes />}
      {error && <WebsiteClaimError title={error.title} description={error.description} onBack={error.onBack} />}
      <ClaimFooter />
    </div>
  )
}

export default WebsiteClaimPage
